import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLicense, activateLicense } from '../services/api';
import './LicenseDetail.css';

// Formatierungsfunktion für Datum und Uhrzeit (konsistent mit LicenseManagement)
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return dateStr;
  }
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
};

function isLicenseExpired(license) {
  if (!license?.expires_at || license.license_type === 'PERMANENT') return false;
  const d = new Date(license.expires_at);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

function LicenseDetail() {
  const { key } = useParams();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const loadLicense = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLicense(key);
      setLicense(data.license);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Lizenz');
    } finally {
      setLoading(false);
    }
  }, [key]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadLicense();
  }, [loadLicense]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!userEmail) {
      setError('Email ist erforderlich');
      return;
    }
    try {
      await activateLicense(key, userEmail);
      loadLicense();
      setUserEmail('');
    } catch (err) {
      setError(err.message || 'Fehler beim Aktivieren');
    }
  };

  if (loading) {
    return <div className="loading">Lade Lizenz-Details...</div>;
  }

  if (error && !license) {
    return <div className="error">{error}</div>;
  }

  if (!license) {
    return <div className="error">Lizenz nicht gefunden</div>;
  }

  const expired = license.license_type !== 'PERMANENT' && isLicenseExpired(license);

  return (
    <div className="license-detail">
      <h2>Lizenz-Details</h2>
      {error && <div className="error-message">{error}</div>}

      {expired && (
        <div
          className="activate-section"
          style={{ marginBottom: '16px', borderLeft: '4px solid #dc2626' }}
        >
          <p style={{ margin: '0 0 12px 0' }}>
            Diese Lizenz ist <strong>abgelaufen</strong>. Eine Verlängerung desselben Schlüssels ist nicht mehr
            möglich. Legen Sie bei Bedarf eine <strong>neue Lizenz</strong> an.
          </p>
          <Link to="/admin/licenses" className="btn btn-primary">
            Zur Lizenzverwaltung (neue Lizenz)
          </Link>
        </div>
      )}

      <div className="detail-card">
        <div className="detail-row">
          <strong>Lizenz-Key:</strong> {license.license_key}
        </div>
        <div className="detail-row">
          <strong>Produktcode:</strong> {license.product_code}
        </div>
        <div className="detail-row">
          <strong>Typ:</strong> {license.license_type}
        </div>
        <div className="detail-row">
          <strong>Benutzer:</strong> {license.user_email || '-'}
        </div>
        <div className="detail-row">
          <strong>Erstellt am:</strong> {formatDate(license.created_at)}
        </div>
        <div className="detail-row">
          <strong>Aktiviert am:</strong> {license.activated_at ? formatDate(license.activated_at) : '-'}
        </div>
        <div className="detail-row">
          <strong>Ablaufdatum:</strong> {license.expires_at ? formatDate(license.expires_at) : 'Unbegrenzt'}
        </div>
        <div className="detail-row">
          <strong>Status:</strong>
          <span className={`badge ${license.is_active ? 'badge-success' : 'badge-danger'}`}>
            {license.is_active ? 'Aktiv' : 'Inaktiv'}
          </span>
        </div>
        <div className="detail-row">
          <strong>Signatur gültig:</strong>
          <span className={`badge ${license.signature_valid ? 'badge-success' : 'badge-danger'}`}>
            {license.signature_valid ? 'Ja' : 'Nein'}
          </span>
        </div>
      </div>

      {!license.user_email && (
        <div className="activate-section">
          <h3>Lizenz aktivieren</h3>
          <form onSubmit={handleActivate}>
            <div className="form-group">
              <label>Benutzer-Email</label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                required
                placeholder="email@example.com"
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Aktivieren
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default LicenseDetail;
